import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Camera, ChevronRight, Loader2, X } from 'lucide-react';

import { ApiError } from '../../api/client';
import { AuthGuard } from '../../app/AuthGuard';
import type { ScreenId } from '../../app/screenRoutes';
import {
  carrierCabinetApi,
  type CarrierProfileDto,
  type OfferDto,
  type TransportDto,
  type TransportRequestDto,
} from '../../features/carrierCabinet/api';
import { CarrierCabinetLayout, navigateCarrier } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { CabinetEmptyState, CabinetLoadState, DateInput } from '../../ui';
import { isCompletedTransport } from '../../features/carrierCabinet/transportStatus';
import {
  dateLabel,
  dateRange,
  hashNumber,
  money,
  PageHeading,
  petDetails,
  PetIcon,
} from './carrierCabinetShared';
import './carrier-offer.css';

export type CarrierOfferScreenId = Extract<ScreenId, 'carrier-offer' | 'carrier-my-offers'>;

const FIGMA_NODES: Record<CarrierOfferScreenId, { '1920': string; '1440': string }> = {
  'carrier-offer': { '1920': '2681:45794', '1440': '2682:58508' },
  'carrier-my-offers': { '1920': '2683:60579', '1440': '2683:61365' },
};

function portfolioPreviewUrl(url: string) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}preview=320`;
}

function openOfferPhotoSwipe(photos: Array<{ url: string; caption?: string; original_filename?: string }>, initialIndex: number) {
  import('photoswipe').then(async ({ default: PhotoSwipe }) => {
    await import('photoswipe/style.css');
    const items = photos.map((photo) => ({
      src: photo.url,
      caption: photo.caption || '',
      alt: photo.caption || photo.original_filename || 'Фото из портфолио',
    }));

    const lightbox = new PhotoSwipe({
      dataSource: items,
      index: initialIndex,
      bgOpacity: 0.92,
      wheelToZoom: true,
      closeOnVerticalDrag: true,
      padding: { top: 40, bottom: 80, left: 30, right: 30 },
      imageClickAction: 'zoom',
      returnFocus: false,
    });

    lightbox.on('gettingData', (event) => {
      const data = event.data;
      if (!data.width || !data.height) {
        const img = new Image();
        img.src = data.src as string;
        if (img.naturalWidth && img.naturalHeight) {
          data.width = img.naturalWidth;
          data.height = img.naturalHeight;
        } else {
          img.onload = () => {
            data.width = img.naturalWidth;
            data.height = img.naturalHeight;
            lightbox.refreshSlideContent(event.index);
          };
        }
      }
    });

    lightbox.init();
  });
}

function PortfolioSelectModal({
  onClose,
  onToggle,
  onUploadNew,
  profile,
  selectedIds,
  uploading,
}: {
  onClose: () => void;
  onToggle: (photo: CarrierProfileDto['portfolio_photos'][number]) => void;
  onUploadNew: (files: File[]) => Promise<void>;
  profile: CarrierProfileDto;
  selectedIds: number[];
  uploading: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  function handleFileInput(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > 0) {
      void onUploadNew(files);
    }
  }

  return createPortal(
    <div
      className="carrier-profile-help__backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <section
        aria-labelledby="carrier-portfolio-modal-title"
        aria-modal="true"
        className="carrier-portfolio-modal"
        role="dialog"
      >
        <div className="carrier-portfolio-modal__header">
          <h2 id="carrier-portfolio-modal-title">Выбрать фото из портфолио</h2>
          <button
            aria-label="Закрыть"
            className="carrier-portfolio-modal__close"
            type="button"
            onClick={onClose}
          >
            <X aria-hidden="true" size={22} strokeWidth={1.5} />
          </button>
        </div>

        <div className="carrier-portfolio-modal__body">
          <input
            ref={fileInputRef}
            hidden
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileInput}
          />
          <div className="carrier-portfolio-modal__grid">
            {profile.portfolio_photos.map((photo) => {
              const isSelected = selectedIds.includes(photo.id);
              return (
                <button
                  key={photo.id}
                  className={`carrier-portfolio-modal__item ${isSelected ? 'is-selected' : ''}`}
                  type="button"
                  title={isSelected ? 'Нажмите, чтобы убрать из отклика' : (photo.caption || 'Нажмите, чтобы добавить в отклик')}
                  onClick={() => onToggle(photo)}
                >
                  <img src={portfolioPreviewUrl(photo.url)} alt={photo.caption || 'Фото портфолио'} />
                  {isSelected ? (
                    <>
                      <span className="carrier-portfolio-modal__remove-cross" aria-hidden="true">✕</span>
                      <span className="carrier-portfolio-modal__badge">
                        <span className="carrier-portfolio-modal__badge-default">✓ Выбрано</span>
                        <span className="carrier-portfolio-modal__badge-hover">✕ Убрать</span>
                      </span>
                    </>
                  ) : null}
                </button>
              );
            })}

            <button
              className={`carrier-portfolio-modal__item carrier-portfolio-modal__item--more ${uploading ? 'is-disabled' : ''}`}
              disabled={uploading}
              type="button"
              aria-label="Загрузить новое фото в портфолио"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <span className="carrier-portfolio-modal__spinner">
                  <Loader2 className="animate-spin" size={20} />
                  <span>Загрузка…</span>
                </span>
              ) : (
                '+ ещё'
              )}
            </button>
          </div>
        </div>

        <div className="carrier-portfolio-modal__footer">
          <span className="carrier-portfolio-modal__count">
            Выбрано: {selectedIds.length}
          </span>
          <button
            type="button"
            className="carrier-portfolio-modal__done-btn"
            onClick={onClose}
          >
            Готово
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

function OfferFormView() {
  const requestId = hashNumber('id');
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Array<{ id: number; url: string; original_filename?: string; caption?: string }>>([]);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [modalUploading, setModalUploading] = useState(false);
  const [price, setPrice] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [expiresDate, setExpiresDate] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');
  const today = new Date().toISOString().split('T')[0];

  function offerErrorMessage(nextError: unknown) {
    let message = nextError instanceof Error ? nextError.message : 'Не удалось отправить отклик.';
    if (nextError instanceof ApiError && nextError.payload && typeof nextError.payload === 'object') {
      const first = Object.values(nextError.payload as Record<string, unknown>)[0];
      const payloadMessage = Array.isArray(first) ? first[0] : first;
      if (typeof payloadMessage === 'string') message = payloadMessage;
    }
    if (/verification is approved/i.test(message)) {
      return profile?.verification_status === 'approved'
        ? 'Погасите блокирующие счета перед отправкой нового отклика.'
        : 'Чтобы отправлять отклики, сначала завершите верификацию перевозчика.';
    }
    if (/already.*offer|unique/i.test(message)) return 'Вы уже отправили отклик на эту заявку.';
    if (/request.*status|not.*open/i.test(message)) return 'Эта заявка уже не принимает новые отклики.';
    return message;
  }

  useEffect(() => {
    let active = true;
    const load = async () => {
      const id = requestId ?? (await carrierCabinetApi.feed())[0]?.id;
      if (!id) return;
      const [nextRequest, nextProfile, offers] = await Promise.all([
        carrierCabinetApi.request(id),
        carrierCabinetApi.profile(),
        carrierCabinetApi.offers(),
      ]);
      if (!active) return;
      if (offers.some((offer) => offer.request === id)) {
        navigateCarrier('carrier-my-offers');
        return;
      }
      setRequest(nextRequest);
      setProfile(nextProfile);
      setSelectedPhotos(nextProfile.portfolio_photos.slice(0, 4));
      setPrice(nextRequest.budget_min_minor ? String(nextRequest.budget_min_minor / 100) : '');

      const initialDeparture = nextRequest.pickup_not_before || new Date().toISOString().split('T')[0];
      setDepartureDate(initialDeparture);
      setDeliveryDate('');
      setExpiresDate('');
      if (!nextProfile.can_offer) {
        setError(nextProfile.verification_status !== 'approved'
          ? 'Чтобы отправлять отклики, сначала завершите верификацию перевозчика.'
          : 'Сейчас отправка откликов недоступна. Проверьте неоплаченные счета в биллинге.');
      }
      setState('ready');
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [requestId]);

  const netPriceMinor = Math.max(0, Math.round(Number(price || 0) * 100));
  const feeMinor = Math.round(netPriceMinor * 0.05);
  const totalMinor = netPriceMinor + feeMinor;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!request) return;
    if (!profile?.can_offer) {
      setError(profile?.verification_status !== 'approved'
        ? 'Чтобы отправлять отклики, сначала завершите верификацию перевозчика.'
        : 'Погасите блокирующие счета перед отправкой нового отклика.');
      return;
    }
    if (!netPriceMinor) {
      setError('Укажите стоимость поездки.');
      return;
    }
    if (!departureDate || !deliveryDate || !expiresDate) {
      setError('Укажите дату выезда, доставки и срок действия отклика.');
      return;
    }
    if (departureDate < today) {
      setError('Дата выезда не может быть раньше сегодняшней даты.');
      return;
    }
    if (deliveryDate < departureDate) {
      setError('Дата доставки не может быть раньше даты выезда.');
      return;
    }
    if (expiresDate < today) {
      setError('Срок действия отклика не может быть раньше сегодняшней даты.');
      return;
    }
    setState('submitting');
    setError('');
    try {
      await carrierCabinetApi.createOffer({
        request: request.id,
        net_price_minor: netPriceMinor,
        currency: request.currency,
        departure_date: departureDate || null,
        delivery_date: deliveryDate || null,
        expires_at: expiresDate ? `${expiresDate}T23:59:59` : null,
        comment,
        portfolio_photo_ids: selectedPhotos.map((p) => p.id),
      });
      navigateCarrier('carrier-my-offers');
    } catch (nextError) {
      setState('error');
      setError(offerErrorMessage(nextError));
    }
  }

  function handleRemovePhoto(photoId: number) {
    setSelectedPhotos((prev) => prev.filter((p) => p.id !== photoId));
  }

  function handleTogglePhoto(photo: CarrierProfileDto['portfolio_photos'][number]) {
    setSelectedPhotos((prev) => {
      if (prev.some((p) => p.id === photo.id)) {
        return prev.filter((p) => p.id !== photo.id);
      }
      return [...prev, photo];
    });
  }

  async function handleModalUpload(files: File[]) {
    setModalUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map((file) => carrierCabinetApi.uploadProfileImage(file, 'portfolio')),
      );
      const nextProfile = await carrierCabinetApi.profile();
      setProfile(nextProfile);
      const newlyUploadedItems = uploaded.map((asset) => ({
        id: asset.id,
        url: asset.url,
        original_filename: asset.original_filename,
        caption: asset.caption || '',
      }));
      setSelectedPhotos((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const toAdd = newlyUploadedItems.filter((p) => !existingIds.has(p.id));
        return [...prev, ...toAdd];
      });
    } catch (err) {
      console.error('Failed to upload portfolio photos from modal', err);
    } finally {
      setModalUploading(false);
    }
  }

  return (
    <div className="carrier-offer" data-state={state}>
      <button className="carrier-back-button" type="button" onClick={() => navigateCarrier('carrier-request-detail', request ? { id: request.id } : undefined)}>← К заявке</button>
      {request && profile ? (
        <form onSubmit={submit}>
          <PageHeading
            className="carrier-offer__top"
            title="Сколько вы хотите за поездку?"
            subtitle={<>{request.from_city} → {request.to_city}<i />{petDetails(request)}<i />#{request.public_code}</>}
          />

          <section className="carrier-offer-card carrier-offer-card--price" data-figma-node="1:22870">
            <h2 data-figma-node="1:22871">Ваша цена</h2>
            <div className="carrier-offer-price-fields" data-figma-node="1:22872">
              <label className="carrier-offer-price-field" data-figma-node="1:22873">
                <span data-figma-node="1:22874">Ваша цена за поездку</span>
                <div className="carrier-offer-price-input-wrap" data-figma-node="1:22875">
                  <div className="carrier-offer-price-field-inline">
                    <span className="carrier-offer-price-sizer" aria-hidden="true">
                      {price ? Number(price).toLocaleString('ru-RU') : '0'}
                    </span>
                    <input
                      required
                      type="text"
                      inputMode="numeric"
                      value={price ? Number(price).toLocaleString('ru-RU') : ''}
                      placeholder="0"
                      onChange={(event) => {
                        const clean = event.target.value.replace(/\D/g, '');
                        setPrice(clean);
                      }}
                      data-figma-node="1:22876"
                    />
                    <span className="carrier-offer-price-currency">{request.currency === 'EUR' ? '€' : '₽'}</span>
                  </div>
                </div>
              </label>
              <div className="carrier-offer-breakdown" data-figma-node="1:22877">
                <div className="carrier-offer-breakdown__row" data-figma-node="1:22878">
                  <span data-figma-node="1:22879">Ваша цена</span>
                  <strong data-figma-node="1:22880">{money(netPriceMinor, request.currency)}</strong>
                </div>
                <div className="carrier-offer-breakdown__row" data-figma-node="1:22881">
                  <span data-figma-node="1:22882">+ Комиссия LOVO (5%)</span>
                  <strong data-figma-node="1:22883">{money(feeMinor, request.currency)}</strong>
                </div>
                <div className="carrier-offer-breakdown__row carrier-offer-breakdown__row--total" data-figma-node="1:22884">
                  <span data-figma-node="1:22885">= Цена для клиента</span>
                  <strong data-figma-node="1:22886">{money(totalMinor, request.currency)}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="carrier-offer-card carrier-offer-card--dates">
            <h2>Сроки</h2>
            <div className="carrier-offer-date-row">
              <label className="carrier-offer-date-field">
                <span>Дата выезда</span>
                <div className="carrier-offer-date-input-wrap">
                  <span className={`carrier-offer-date-display ${departureDate ? '' : 'is-placeholder'}`}>
                    {departureDate ? dateLabel(departureDate) : 'дд.мм.гггг'}
                  </span>
                  <DateInput min={today} value={departureDate} onChange={(event) => { setError(''); setDepartureDate(event.target.value); }} />
                </div>
              </label>

              <label className="carrier-offer-date-field">
                <span>Дата доставки</span>
                <div className="carrier-offer-date-input-wrap">
                  <span className={`carrier-offer-date-display ${deliveryDate ? '' : 'is-placeholder'}`}>
                    {deliveryDate ? dateLabel(deliveryDate) : 'дд.мм.гггг'}
                  </span>
                  <DateInput min={departureDate || today} value={deliveryDate} onChange={(event) => { setError(''); setDeliveryDate(event.target.value); }} />
                </div>
              </label>

              <label className="carrier-offer-date-field">
                <span>Отклик актуален до</span>
                <div className="carrier-offer-date-input-wrap">
                  <span className={`carrier-offer-date-display ${expiresDate ? '' : 'is-placeholder'}`}>
                    {expiresDate ? dateLabel(expiresDate) : 'дд.мм.гггг'}
                  </span>
                  <DateInput min={today} value={expiresDate} onChange={(event) => { setError(''); setExpiresDate(event.target.value); }} />
                </div>
                <small>После этой даты отклик автоматически скроется из списка заказчика</small>
              </label>
            </div>
          </section>

          <section className="carrier-offer-card carrier-offer-card--comment">
            <h2>Комментарий заказчику</h2>
            <textarea value={comment} placeholder="Расскажите о своём опыте перевозки животных, условиях в пути и т.д." onChange={(event) => setComment(event.target.value)} />
          </section>

          <section className="carrier-offer-card carrier-offer-card--photos">
            <h2>Фото из портфолио</h2>
            <div className="carrier-offer-thumbs">
              {selectedPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="carrier-offer-thumb carrier-offer-thumb--photo"
                  role="button"
                  tabIndex={0}
                  title="Нажмите для просмотра в полном размере"
                  onClick={() => openOfferPhotoSwipe(selectedPhotos, index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openOfferPhotoSwipe(selectedPhotos, index);
                    }
                  }}
                >
                  <img src={portfolioPreviewUrl(photo.url)} alt={`Фото из портфолио ${index + 1}`} />
                  <button
                    className="carrier-offer-thumb__remove"
                    type="button"
                    aria-label="Убрать фото из отклика"
                    title="Убрать фото"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePhoto(photo.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {selectedPhotos.length < 8 && (
                <button
                  className="carrier-offer-thumb carrier-offer-thumb--add"
                  type="button"
                  aria-label="Выбрать фото из портфолио"
                  title="Добавить фото из портфолио"
                  onClick={() => setPortfolioModalOpen(true)}
                >
                  <Camera aria-hidden="true" size={24} strokeWidth={1.5} />
                </button>
              )}
              <button
                className="carrier-offer-thumb carrier-offer-thumb--more"
                type="button"
                aria-label="Выбрать фото из портфолио"
                onClick={() => setPortfolioModalOpen(true)}
              >
                + ещё
              </button>
            </div>
            {portfolioModalOpen && (
              <PortfolioSelectModal
                profile={profile}
                selectedIds={selectedPhotos.map((p) => p.id)}
                uploading={modalUploading}
                onClose={() => setPortfolioModalOpen(false)}
                onToggle={handleTogglePhoto}
                onUploadNew={handleModalUpload}
              />
            )}
          </section>

          <section className="carrier-offer-info" data-figma-node="1:22915">
            <p className="carrier-offer-info__title" data-figma-node="1:22916">
              К отклику автоматически прикрепится:
            </p>
            <div className="carrier-offer-info__chips" data-figma-node="1:22917">
              <span data-figma-node="1:22918">★ Рейтинг {Number(profile.review_rating).toFixed(1)}</span>
              <span data-figma-node="1:22919">
                {profile.verification_status === 'approved' ? '✓ Верифицирован' : 'Верификация не завершена'}
              </span>
              <span data-figma-node="1:22920">{profile.completed_transports_count} перевозок</span>
              <span data-figma-node="1:22921">{profile.photo_rating_percent}% рейтинг фото</span>
            </div>
          </section>

          <div className={`carrier-offer-actions${error ? ' has-error' : ''}`}>
            {error ? <p className="carrier-offer-error" role="alert">{error}</p> : null}
            <button className="carrier-outline-button" type="button" onClick={() => navigateCarrier('carrier-request-detail', { id: request.id })}>Отмена</button>
            {profile.can_offer ? (
              <button className="carrier-green-button" disabled={state === 'submitting'} type="submit">{state === 'submitting' ? 'Отправляем…' : 'Откликнуться'}</button>
            ) : profile.verification_status !== 'approved' ? (
              <button className="carrier-green-button" type="button" onClick={() => navigateCarrier('carrier-verify')}>Пройти верификацию</button>
            ) : (
              <button className="carrier-green-button" type="button" onClick={() => navigateCarrier('carrier-earnings')}>К счетам и балансу</button>
            )}
          </div>
        </form>
      ) : null}
    </div>
  );
}

type OffersTab = 'all' | 'waiting' | 'accepted' | 'declined' | 'completed';

function statusGroup(status: string, completed = false): Exclude<OffersTab, 'all'> {
  if (status === 'accepted' && completed) return 'completed';
  if (status === 'accepted') return 'accepted';
  if (['declined', 'not_selected', 'expired', 'cancelled'].includes(status)) return 'declined';
  return 'waiting';
}

function statusLabel(status: string, completed = false) {
  if (status === 'accepted' && completed) return 'Завершено';
  if (status === 'accepted') return 'Принят';
  if (status === 'expired') return 'Истёк';
  if (status === 'cancelled') return 'Отменён';
  if (['declined', 'not_selected'].includes(status)) return 'Отклонён';
  return 'Ожидает';
}

function MyOfferCard({ offer, request, transport }: { offer: OfferDto; request: TransportRequestDto; transport?: TransportDto }) {
  const completed = request.status === 'completed' || Boolean(transport && isCompletedTransport(transport));
  const inProgress = offer.status === 'accepted' && transport && !completed;
  const expanded = Boolean(inProgress);

  function open() {
    if (transport) navigateCarrier('carrier-active-transport', { id: transport.id });
    else navigateCarrier('carrier-request-detail', { id: request.id });
  }

  return (
    <button className={`carrier-my-offer-card${expanded ? ' carrier-my-offer-card--expanded' : ''}`} type="button" aria-label={`Открыть отклик ${request.from_city} — ${request.to_city}`} onClick={open}>
      <div className="carrier-my-offer-card__content">
        <div className="carrier-my-offer-card__route">
          <strong>{request.from_city}</strong><ChevronRight aria-hidden="true" size={16} strokeWidth={1.5} /><strong>{request.to_city}</strong>
          {request.public_code ? <><i /><span>#{request.public_code.replace(/^#/, '')}</span></> : null}
          {inProgress ? <span className="carrier-my-offer-card__transport-status"><em>В пути</em></span> : null}
        </div>
        <div className="carrier-my-offer-card__details">
          <PetIcon type={request.pets[0]?.pet_type ?? 'other'} />
          <span>{petDetails(request)}</span><i /><span>{dateRange(request)}</span>
        </div>
        <strong className="carrier-my-offer-card__price">{money(offer.net_price_minor, offer.currency)}</strong>
      </div>
      <span className={`carrier-my-offer-card__status is-${statusGroup(offer.status, completed)}`}>{statusLabel(offer.status, completed)}</span>
    </button>
  );
}

function MyOffersView() {
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [requests, setRequests] = useState(new Map<number, TransportRequestDto>());
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [tab, setTab] = useState<OffersTab>('all');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [nextOffers, nextTransports] = await Promise.all([
        carrierCabinetApi.offers(),
        carrierCabinetApi.transports(),
      ]);
      const uniqueRequestIds = [...new Set(nextOffers.map((offer) => offer.request))];
      const requestResults = await Promise.all(
        uniqueRequestIds.map(async (id) => [id, await carrierCabinetApi.request(id)] as const),
      );
      const requestMap = new Map<number, TransportRequestDto>();
      for (const [id, request] of requestResults) requestMap.set(id, request);
      if (!active) return;
      setOffers(nextOffers);
      setRequests(requestMap);
      setTransports(nextTransports);
      setState('ready');
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  const groups = useMemo(() => new Map(offers.map((offer) => {
    const transport = transports.find((item) => item.request_id === offer.request);
    const completed = requests.get(offer.request)?.status === 'completed' || Boolean(transport && isCompletedTransport(transport));
    return [offer.id, statusGroup(offer.status, completed)];
  })), [offers, requests, transports]);
  const counts = {
    all: offers.length,
    waiting: offers.filter((offer) => groups.get(offer.id) === 'waiting').length,
    accepted: offers.filter((offer) => groups.get(offer.id) === 'accepted').length,
    declined: offers.filter((offer) => groups.get(offer.id) === 'declined').length,
    completed: offers.filter((offer) => groups.get(offer.id) === 'completed').length,
  };
  const visible = (tab === 'all' ? offers : offers.filter((offer) => groups.get(offer.id) === tab))
    .slice()
    .sort((left, right) => {
      const order = { waiting: 0, accepted: 1, completed: 2, declined: 3 };
      return order[groups.get(left.id)!] - order[groups.get(right.id)!];
    });

  return (
    <div className="carrier-my-offers" data-state={state}>
      <PageHeading title="Мои отклики" subtitle="Статус ваших предложений по заявкам заказчиков" />
      <div className="carrier-my-offers__tabs" role="tablist" aria-label="Фильтр откликов">
        {([
          ['all', 'Все'], ['waiting', 'Ожидают'], ['accepted', 'Приняты'], ['declined', 'Отклонены'], ['completed', 'Завершено'],
        ] as Array<[OffersTab, string]>).map(([value, label]) => (
          <button className={tab === value ? 'is-active' : ''} key={value} role="tab" aria-selected={tab === value} type="button" onClick={() => setTab(value)}>
            <span>{label}</span>
            <span className="carrier-my-offers__bullet">•</span>
            <b>{counts[value]}</b>
          </button>
        ))}
      </div>
      <div className="carrier-my-offers__list">
        <CabinetLoadState state={state} />
        {state === 'ready' && visible.length === 0 ? <CabinetEmptyState kind="offers" filtered={offers.length > 0} /> : null}
        {visible.map((offer) => {
          const request = requests.get(offer.request);
          if (!request) return null;
          return <MyOfferCard key={offer.id} offer={offer} request={request} transport={transports.find((item) => item.request_id === request.id)} />;
        })}
      </div>
    </div>
  );
}

export function CarrierOfferScreen({ screen }: { screen: CarrierOfferScreenId }) {
  return (
    <AuthGuard allow={['carrier']}>
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen]} screen={screen}>
        {screen === 'carrier-offer' ? <OfferFormView /> : <MyOffersView />}
      </CarrierCabinetLayout>
    </AuthGuard>
  );
}
